import { formatDate } from '@angular/common';
import { Component, Output } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import FileSaver from 'file-saver';
import { SideNavService } from 'src/app/core-flc/sidenav/sidenav.service';
import { RouterLinkEnum } from 'src/app/enums/router-link-enum';
import { LayerSwitcherService } from 'src/app/map-openlayers/layerswitcher/layerswitcher.service';
import { GravelCalcType, TunnelSectionDto } from 'src/app/server-api/models';
import {
  PdfExportService,
  RasterControllerService,
  TunnelSectionControllerService,
} from 'src/app/server-api/services';
import { AlertComponent } from 'src/app/utils-flc/alert/alert.component';
import { DetailComponent } from 'src/app/utils-flc/detail/detail.component';
import { DialogRecalucateQueueComponent } from '../dialog-recalucate-queue/dialog-recalucate-queue.component';
import { ITunnelSectionFormGroup } from './ITunnelSectionFormGroup';

@Component({
  selector: 'app-tunnel-sections',
  templateUrl: './tunnel-sections.component.html',
  styleUrls: ['./tunnel-sections.component.css'],
  providers: [LayerSwitcherService],
})
export class TunnelSectionsComponent extends DetailComponent {
  section: TunnelSectionDto;
  step: number = 3;
  queueTitle: string;
  itemsLongitudinalPosition: number[];
  itemsTransversalPosition: number[];
  itemsPrefix: string[];
  itemsBermPosition: number[];
  rasterExportRunning: boolean = false;
  rasterExportRunningTitle: string;

  selectedTypeGravelCalc: GravelCalcType[] = [];
  @Output() preSelectedTypeGravelCalc: GravelCalcType[] = [];

  constructor(
    public override translate: TranslateService,
    private tunnelSectionService: TunnelSectionControllerService,
    private rasterService: RasterControllerService,
    public override sidenavService: SideNavService,
    public override router: Router,
    private formBuilder: UntypedFormBuilder,
    public lsService: LayerSwitcherService,
    private pdfExportService: PdfExportService
  ) {
    super(translate, sidenavService, router);
  }

  override initItem(): void {
    super.initItem();
    //init an empty dto and set the invisible fields
    this.section = {} as any as TunnelSectionDto;

    this.initForm();
  }

  override ngOnInit() {
    this.lsService.setShowLegendsButton(true);
    this.profilesAllowEdit = 'ADMIN,ENGINEER';
    super.ngOnInit();
  }

  override getItem(request) {
    super.getItem(request);

    this.tunnelSectionService.getTunnelSection(request).subscribe(
      (data: TunnelSectionDto) => {
        this.section = data;
        this.name = data.name;
        if (!this.section.longitudinalPosition) {
          this.section.longitudinalPosition = [];
          this.section.longitudinalPosition[0] = 0;
        }
        if (!this.section.transversalPosition) {
          this.section.transversalPosition = [];
          this.section.transversalPosition[0] = 0;
        }
        if (!this.section.bermPositions) {
          this.section.bermPositions = [];
          this.itemsPrefix = [];
          this.section.bermPositions[0] = 0;
        }
        //to fix to many digits from server sometimes
        this.section.length = Math.round(this.section.length * 1000) / 1000;
        this.section.width = Math.round(this.section.width * 1000) / 1000;
        this.section.beginSectionDepth =
          Math.round(this.section.beginSectionDepth * 1000) / 1000;
        this.section.endSectionDepth =
          Math.round(this.section.endSectionDepth * 1000) / 1000;
        this.section.designBeginSectionDepth =
          Math.round(this.section.designBeginSectionDepth * 1000) / 1000;
        this.section.designEndSectionDepth =
          Math.round(this.section.designEndSectionDepth * 1000) / 1000;
        this.section.minDepthSoil =
          Math.round(this.section.minDepthSoil * 1000) / 1000;
        this.section.maxDepthSoil =
          Math.round(this.section.maxDepthSoil * 1000) / 1000;
        this.section.maxGravelThickness =
          Math.round(this.section.maxGravelThickness * 1000) / 1000;
        this.section.minGravelThickness =
          Math.round(this.section.minGravelThickness * 1000) / 1000;
        this.section.maxPrecSpecs =
          Math.round(this.section.maxPrecSpecs * 1000) / 1000;
        this.itemsLongitudinalPosition = [];
        this.section.longitudinalPosition.forEach((v) => {
          this.itemsLongitudinalPosition.push(Math.round(v * 1000) / 1000);
        });
        this.itemsTransversalPosition = [];
        this.section.transversalPosition.forEach((v) => {
          this.itemsTransversalPosition.push(Math.round(v * 1000) / 1000);
        });
        let i = 1;
        this.itemsBermPosition = [];
        this.itemsPrefix = [];
        this.section.bermPositions.forEach((v) => {
          this.itemsBermPosition.push(Math.round(v * 1000) / 1000);
          this.itemsPrefix.push(i.toString());
          i++;
        });

        this.section.stressPatternAvg =
          Math.round(this.section.stressPatternAvg * 1000) / 1000;
        this.section.gravelSpacing =
          Math.round(this.section.gravelSpacing * 1000) / 1000;
        this.section.gravelStartPosition =
          Math.round(this.section.gravelStartPosition * 1000) / 1000;
        this.section.gravelAvgAreaPerct =
          Math.round(this.section.gravelAvgAreaPerct * 1000) / 1000;

        this.selectedTypeGravelCalc[0] = this.section.gravelCalc;
        this.preSelectedTypeGravelCalc = this.selectedTypeGravelCalc;

        this.section.gravelTopDiffTunnelBottomAvg =
          Math.round(this.section.gravelTopDiffTunnelBottomAvg * 1000) / 1000;
        this.section.tunnelDisplacementAvg =
          Math.round(this.section.tunnelDisplacementAvg * 1000) / 1000;

        this.initForm();
      },
      (err: string) => {
        window.alert(err.replace('Object', 'Tunnel section'));
      }
    );
  }

  setStep(index: number) {
    this.step = index;
  }

  override setForm() {
    //set visibility fields, set FormGroup and validators
    super.setForm();

    this.columnsVisible = new Map([
      ['id', false],
      ['name', true],
      ['created', true],
      ['lastModificationDate', true],
      ['length', false],
      ['width', false],
      ['startChainage', true],
      ['beginSectionDepth', true],
      ['endSectionDepth', true],
      ['longitudinalPosition', true],
      ['transversalPosition', true],
      ['bermPosition', true],
      ['kDistSoil', false],
      ['kSubSoil', false],
      ['calcStatus', true],
      ['tunnelStatus', true],
      ['latestError', true],
      ['designBeginSectionDepth', false],
      ['designEndSectionDepth', false],
      ['minDepthSoil', false],
      ['maxDepthSoil', true],
      ['maxGravelThickness', false],
      ['minGravelThickness', true],
      ['stressPatternAvg', false],
      ['gravelCalc', !this.disableByProfile],
      ['gravelCalc_disabled', this.disableByProfile],
      ['gravelSpacing', true],
      ['gravelStartPosition', true],
      ['gravelAvgAreaPerct', true],
      ['tunnelDisplacementAvg', true],
      ['gravelTopDiffTunnelBottomAvg', false],
      ['maxPrecSpecs', true],
      ['d1', true],
      ['d2', true],
      ['calculationInProgress', true]
    ]);

    this.detailForm = this.formBuilder.group({
      id: [''],
      name: [
        { value: '', disabled: this.disableByProfile },
        Validators.required,
      ],
      created: { value: '', disabled: true },
      lastModificationDate: { value: '', disabled: true },
      length: { value: '0', disabled: true },
      width: { value: '0', disabled: true },
      startChainage: { value: '0', disabled: true },
      beginSectionDepth: { value: 0, disabled: this.disableByProfile },
      endSectionDepth: { value: 0, disabled: this.disableByProfile },
      kDistSoil: { value: 0, disabled: this.disableByProfile },
      kSubSoil: { value: 0, disabled: this.disableByProfile },
      calcStatus: { value: '', disabled: true },
      tunnelStatus: { value: '', disabled: true },
      latestError: { value: '', disabled: true },
      designBeginSectionDepth: { value: 0, disabled: true },
      designEndSectionDepth: { value: 0, disabled: true },
      minDepthSoil: { value: 0, disabled: true },
      maxDepthSoil: { value: 0, disabled: true },
      maxGravelThickness: { value: 0, disabled: true },
      minGravelThickness: { value: 0, disabled: true },
      stressPatternAvg: { value: 0, disabled: true },
      gravelCalc: { value: '', disabled: this.disableByProfile },
      gravelCalc_disabled: { value: '', disabled: this.disableByProfile },
      gravelSpacing: { value: 0, disabled: this.disableByProfile },
      gravelStartPosition: { value: 0, disabled: this.disableByProfile },
      gravelAvgAreaPerct: { value: 0, disabled: true },
      gravelTopDiffTunnelBottomAvg: { value: 0, disabled: true },
      tunnelDisplacementAvg: { value: 0, disabled: true },
      maxPrecSpecs: { value: 0, disabled: true },
      d1: { value: 0, disabled: true },
      d2: { value: 0, disabled: true },
      calculationInProgress: { value: false, disabled: true}
    }) as ITunnelSectionFormGroup;

    //patch value dto to formGroup
    this.detailForm.patchValue(this.section);
    this.detailForm.controls['gravelCalc_disabled'].setValue(
      this.section.gravelCalc
    );

    this.lsService.toAddSectionLayer(this.section.id);
  }

  override initForm() {
    super.initForm();
    this.initDialogDelete('tunnel-section.dialog_title');

    //set topmenu icon actions
    this.translate
      .get([
        'icon_download_excel',
        'icon_refresh',
        'icon_menu',
        'icon_next',
        'icon_previous',
        'icon_print_tunnelsection',
        'recalculate-queue.title',
        'icon_export_raster',
        'raster_running_title',
      ])
      .subscribe((translations: string[]) => {
        this.rasterExportRunningTitle = translations['raster_running_title'];
        this.queueTitle = translations['recalculate-queue.title'];
        this.topMenuIcons = [
          {
            icon: 'download',
            title: translations['icon_download_excel'],
            routerLink: null,
            clickMethod: 'excel',
            hide: false,
          },
          {
            icon: 'print',
            title: translations['icon_print_tunnelsection'],
            routerLink: null,
            clickMethod: 'print',
            hide: false,
          },
          {
            icon: ' system_update_alt ',
            title: translations['icon_export_raster'],
            routerLink: null,
            clickMethod: 'rasterExport',
            hide: false,
          },
          {
            icon: 'refresh',
            title: translations['icon_refresh'],
            routerLink: null,
            clickMethod: 'refresh',
            hide: false
          },
          {
            icon: 'arrow_back',
            title: translations['icon_previous'],
            routerLink: null,
            clickMethod: 'navigatePrevious',
            hide: this.hidePrevious
          },
          {
            icon: 'arrow_forward',
            title: translations['icon_next'],
            routerLink: null,
            clickMethod: 'navigateNext',
            hide: this.hideNext
          },
          {
            icon: 'menu',
            title: translations['icon_menu'],
            routerLink: RouterLinkEnum.TUNNEL_SECTIONS_DASHBOARD.toString(),
            clickMethod: 'dummy',
            hide: false
          },
        ];
      });
    this.setForm();
  }

  override saveOk() {
    super.saveOk();
    this.section.name = this.detailForm.controls['name'].value;
    this.section.beginSectionDepth =
      this.detailForm.controls['beginSectionDepth'].value;
    this.section.endSectionDepth =
      this.detailForm.controls['endSectionDepth'].value;
    this.section.kDistSoil = this.detailForm.controls['kDistSoil'].value;
    this.section.kSubSoil = this.detailForm.controls['kSubSoil'].value;
    this.section.gravelCalc = this.selectedTypeGravelCalc[0];
    this.section.gravelSpacing =
      this.detailForm.controls['gravelSpacing'].value;
    this.section.gravelStartPosition =
      this.detailForm.controls['gravelStartPosition'].value;

    let error = false;
    this.section.longitudinalPosition.forEach((v) => {
      if (!error && v >= this.section.length) {
        let errorMessage = this.translate.instant(
          'tunnel-section.error-longitudinalPosition',
          { value: this.section.length }
        );
        error = true;
        window.alert(errorMessage);
      }
    });
    this.section.transversalPosition.forEach((v) => {
      if (!error && v >= this.section.width) {
        let errorMessage = this.translate.instant(
          'tunnel-section.error-transversalPosition',
          { value: this.section.width }
        );
        error = true;
        window.alert(errorMessage);
      }
    });



    if (!error) {
      var response =
        this.id !== 'new'
          ? this.tunnelSectionService.updateTunnelSection({
              id: this.id,
              body: this.section,
            })
          : this.tunnelSectionService.createTunnelSection({
              body: this.section,
            });

      response.subscribe(
        (data) => {
          this.sidenavService.setLoading(false);
          this.id = data.id;
          this.name = data.name;
          this.refresh();
        },
        (err) => {
          this.sidenavService.setLoading(false);
          console.log('Tunnel section not created/updated');
          window.alert('Create/update tunnel section not possible');
        }
      );
    }
  }

  initBermPositions() {
    this.itemsBermPosition = [];
    this.itemsPrefix = [];
    this.changeBermPosition(this.itemsBermPosition);
  }

  async pasteBermPositions() {
    try {
      let clipboardData = await navigator.clipboard.readText();
      // Split rows (Excel rows)
      const rows = clipboardData.split(/\r?\n/).filter((r) => r.trim() !== '');

      rows.forEach((row) => {
        // Split columns (Excel columns separated by tabs)
        const cols = row.split('\t');
        cols.forEach((col) => {
          this.itemsBermPosition.push(this.parseNumber(col.trim()));
          this.itemsPrefix.push(this.itemsBermPosition.length.toString());
        });
      });
      this.changeBermPosition(this.itemsBermPosition);

    } catch (err) {
      console.error('Clipboard read failed', err);
    }
  }

  parseNumber(value: string): number | null {
    if (!value) return null;

    let v = value.trim();

    // Remove currency symbols and %
    v = v.replace(/[€$£%]/g, '');

    // Handle accounting negatives: (1234) → -1234
    if (/^\(.*\)$/.test(v)) {
      v = '-' + v.slice(1, -1);
    }

    // Remove spaces (thousands)
    v = v.replace(/\s/g, '');

    const hasComma = v.includes(',');
    const hasDot = v.includes('.');

    if (hasComma && hasDot) {
      // Decide based on last separator
      if (v.lastIndexOf(',') > v.lastIndexOf('.')) {
        // EU: 1.234,56
        v = v.replace(/\./g, '').replace(',', '.');
      } else {
        // US: 1,234.56
        v = v.replace(/,/g, '');
      }
    } else if (hasComma) {
      // Assume comma is decimal
      v = v.replace(',', '.');
    } else {
      // Dot or plain number → OK
    }

    const num = Number(v);
    return isNaN(num) ? null : num;
  }

  override delete() {
    super.delete();
    this.tunnelSectionService.deleteTunnelSection({ id: this.id }).subscribe(
      (data) => {
        this.sidenavService.setLoading(false);
        this.router.navigateByUrl(
          RouterLinkEnum.TUNNEL_SECTIONS_DASHBOARD.toString()
        );
        console.log('item deleted successfully!');
      },
      (error) => {
        this.sidenavService.setLoading(false);
        console.log(error.error.message);
        window.alert('Delete tunnel section not possible');
      }
    );
  }

  excel() {
    this.sidenavService.setLoading(true);
    this.tunnelSectionService.getExcelFile({ id: this.id }).subscribe(
      (data) => {
        this.sidenavService.setLoading(false);

        let fileName =
          'tunnel-section_' +
          formatDate(new Date(), 'yyyy-MM-dd_hh:mm', 'en') +
          '.xlsx';
        FileSaver.saveAs(data, fileName);
      },
      (error) => {
        this.sidenavService.setLoading(false);
        window.alert(
          this.translate.instant('tunnel-section.excel-download-failed')
        );
      }
    );
  }

  print() {
    this.sidenavService.setLoading(true);

    this.pdfExportService.exportTunnelSection({ id: this.id }).subscribe(
      (pdf) => {
        this.sidenavService.setLoading(false);

        let fileName =
          'Trench_Assessment_' +
          this.section.name.replaceAll(' ', '_') +
          '_' +
          formatDate(new Date(), 'yyyy-MM-dd_hh:mm', 'en') +
          '.pdf';
        FileSaver.saveAs(pdf, fileName);
      },
      (error) => {
        this.sidenavService.setLoading(false);
        window.alert(this.translate.instant('tunnel-section.print-failed'));
      }
    );
  }

  rasterExport() {
    this.rasterExportRunning = true;
    this.rasterService
      .getRasterForTunnelSection({ sectionId: this.id })
      .subscribe(
        (data) => {
          this.rasterExportRunning = false;

          let fileName =
            'rasters_' +
            this.section.name.replace(':', '-') +
            '_' +
            formatDate(new Date(), 'yyyy-MM-dd_hh:mm', 'en') +
            '.xlsx';
          FileSaver.saveAs(data, fileName);
        },
        (error) => {
          this.rasterExportRunning = false;
          window.alert(
            this.translate.instant('tunnel-section.raster-download-failed')
          );
        }
      );
  }

  dragEnd(unit, { sizes }) {
    this.lsService.onResizeContainer();
  }

  recalculateSection() {
    //use a confirmDialog box before recalculate
    let recalculateConfirmText = this.translate.instant(
      'tunnel-section.recalculate-confirm',
      { name: this.name }
    );
    let recalculateDialogTitleText = this.translate.instant(
      'tunnel-section.recalculate-dialog-title'
    );
    const recalculateConfirmDialog = this.dialog.open(AlertComponent, {
      data: {
        message: recalculateConfirmText,
        type: recalculateDialogTitleText,
        buttonCancel: this.buttonCancel,
        buttonConfirm: this.buttonConfirm,
      },
      hasBackdrop: true,
    });
    recalculateConfirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.recalculate();
      }
    });
  }

  recalculate() {
    this.sidenavService.setLoading(true);
    this.tunnelSectionService.calculate({ id: this.id }).subscribe(
      (data) => {
        this.sidenavService.setLoading(false);
        this.refresh();
        this.showPopupQueue();
      },
      (error) => {
        window.alert(
          this.translate.instant('tunnel-section.recalculate-failed')
        );
        this.sidenavService.setLoading(false);
      }
    );
  }

  showPopupQueue() {
    let popupDialog = this.dialog.open(DialogRecalucateQueueComponent, {
      data: { buttonCancel: 'Close' },
      hasBackdrop: true,
    });
    popupDialog.afterClosed().subscribe((result) => {
      popupDialog = null;
    });
  }

  changeLongitudinalPosition(items: number[]) {
    this.section.longitudinalPosition = items.sort(function (a, b) {
      return a - b;
    });
  }

  changeTransversalPosition(items: number[]) {
    this.section.transversalPosition = items.sort(function (a, b) {
      return a - b;
    });
  }

  changeBermPosition(items: number[]) {

    this.section.bermPositions = items.sort(function (a, b) {
      return a - b;
    });
  }

  setSelectedTypeGravelCalc(types: GravelCalcType[]) {
    this.selectedTypeGravelCalc = types;
  }

  //https://www.javachinna.com/angular-spring-boot-crud-app/
}
